import z from 'zod';
import { getRevalidateTime } from '../../../utils/cache';
import { getEnvVar } from '../../../utils/env';
import { Asset } from '../_assets';
import { Category } from '../_categories';

// Debug utility function to control console output
const debug = {
  isEnabled: () => {
    return false; // should be a global thing env variable, but for now just locally
  },
  log: (...args: unknown[]) => {
    if (debug.isEnabled()) {
      console.log('[DEBUG]', ...args);
    }
  },
  warn: (...args: unknown[]) => {
    if (debug.isEnabled()) {
      console.warn('[DEBUG]', ...args);
    }
  },
  error: (...args: unknown[]) => {
    // Always log errors, but add debug prefix when in debug mode
    if (debug.isEnabled()) {
      console.error('[DEBUG]', ...args);
    } else {
      console.error(...args);
    }
  },
};

// TODO: ensure selected language is always put into header 'Accept-Language' when fetching lists

type Page = { number?: number; size: number };

type NextFetchRequestInit = RequestInit & {
  next?: {
    revalidate?: number;
    tags?: string[];
  };
};

export const getList = async (
  alias: string,
  acceptLanguage?: string,
  depth = 3,
  page?: Page,
  maxRetries = 1, // Add param to control number of retries, default to just 1
  timeoutMs = 8000, // Default timeout of 8 seconds
): Promise<ListType | undefined> => {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };

  if (acceptLanguage) {
    headers['accept-language'] = acceptLanguage.replace('_', '-');
  }
  const requestOptions: NextFetchRequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
    cache: 'reload', // Force client-side revalidation with server
    next: {
      revalidate: getRevalidateTime(),
      tags: acceptLanguage
        ? [`list-${alias}-${acceptLanguage}`]
        : [`list-${alias}`],
    },
  };
  let uri = `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/lists/${alias}?depth=${depth}`;

  // CRITICAL: Add locale to URL to ensure different locales are cached separately
  // This is essential for both Next.js cache and browser HTTP cache
  if (acceptLanguage) {
    uri += `&locale=${encodeURIComponent(acceptLanguage)}`;
  }
  if (page?.number) {
    uri += `&childrenPage[number]=${page.number}`;
  }
  if (page?.size) {
    uri += `&childrenPage[size]=${page.size}`;
  }
  //const uri = `http://172.24.1.208:3001/api/slow-response`;
  // Track retries globally - moved outside of attemptFetch to prevent reset
  let retryCount = 0;
  const maxAttempts = maxRetries + 1; // +1 for the initial attempt
  const startTime = Date.now(); // Track overall start time

  // Implement a fetchWithTimeout function to handle timeouts
  const fetchWithTimeout = async (
    url: string,
    options: RequestInit,
    timeout: number,
  ): Promise<Response> => {
    const controller = new AbortController();
    const { signal } = controller;

    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (
        error &&
        typeof error === 'object' &&
        'name' in error &&
        error.name === 'AbortError'
      ) {
        throw new Error(`Request timed out after ${timeout}ms`);
      }
      throw error;
    }
  };

  while (retryCount < maxAttempts) {
    try {
      const attemptStartTime = Date.now();
      debug.log(
        `[TIMER] Starting fetch request to ${uri} (attempt ${retryCount + 1}/${maxAttempts})`,
      );

      // Use the fetchWithTimeout helper
      const response = await fetchWithTimeout(uri, requestOptions, timeoutMs);

      const requestTime = Date.now() - attemptStartTime;
      debug.log(`[TIMER] Fetch request completed in ${requestTime}ms`);
      debug.log('Response status:', response.status, response.statusText);
      debug.log(
        'Response headers:',
        JSON.stringify(Object.fromEntries([...response.headers.entries()])),
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error ${response.status}: ${response.statusText}`,
        );
      }

      // Parse JSON with error handling
      try {
        const json = (await response.json()) as {
          data: ListType[] | undefined;
        };
        return json?.data?.[0];
      } catch (jsonError) {
        debug.error('JSON parsing error:', jsonError);
        // Try to get response text for debugging
        try {
          const responseClone = response.clone();
          const text = await responseClone.text();
          debug.error(
            'Response text that could not be parsed:',
            text.substring(0, 500) + (text.length > 500 ? '...' : ''),
          );
        } catch (textError) {
          debug.error('Could not get response text:', textError);
        }
        throw new Error('Failed to parse response JSON: ' + String(jsonError));
      }
    } catch (error) {
      retryCount++; // Increment retry count here

      const totalElapsed = Date.now() - startTime;

      // Check if we have retries left
      if (retryCount < maxAttempts) {
        debug.warn(`Request failed, retrying (${retryCount}/${maxRetries})...`);

        // Calculate backoff time with jitter to prevent thundering herd
        const backoffTime = Math.min(1000 * Math.pow(2, retryCount - 1), 8000);
        const jitter = Math.random() * 500; // Add 0-500ms random jitter
        const waitTime = backoffTime + jitter;

        debug.log(`Waiting ${waitTime}ms before retry...`);
        await new Promise((resolve) => setTimeout(resolve, waitTime));

        // Continue to next iteration (will retry)
        continue;
      }

      // If we've exhausted all retries, log and rethrow
      debug.error(
        `[ERROR] Request failed after ${retryCount - 1} retries:`,
        error,
      );
      debug.error(
        `[TIMER] Total time taken for all attempts: ${totalElapsed}ms`,
      );
      debug.error('Error:', uri, error);
      throw error;
    }
  }

  // This line should never be reached, but TypeScript needs a return
  return undefined;
};

export const getPlaylist = (playlistId: string, acceptLanguage?: string) => {
  const headers: Record<string, string> = {
    accept: 'application/json',
  };

  if (acceptLanguage) {
    headers['accept-language'] = acceptLanguage;
  }
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.content-discovery.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/lists/${playlistId}`,
    requestOptions,
  )
    .then((res) => {
      return res.json();
    })
    .then((json) => {
      const response = responseValidator.parse(json);
      if (response.errors) {
        throw new Error(response.errors[0]?.detail);
      }
      return response.data ? (response.data[0] as PlaylistType) : undefined;
    });
};

const Image = z.object({
  defaultUrl: z.string(),
  version: z.string(),
  templates: z.object({
    regions: z.array(z.string()),
    locations: z.array(z.string()),
    withRegion: z.string(),
    withLocation: z.string(),
    complete: z.string(),
  }),
});

const Product = z.object({
  id: z.string(),
  name: z.string(),
  accessType: z.string(),
});

const CategoryElement = z.object({
  id: z.string(),
  timestamp: z.string(),
  images: Image,
  categoryType: z.union([
    z.literal('SPORT'),
    z.literal('COMPETITION'),
    z.literal('SEASON'),
    z.literal('NEWS'),
    z.literal('NEWS_PROGRAM'),
    z.literal('ENTERTAINMENT'),
    z.literal('MOVIE_CATALOG'),
    z.literal('SHOW_CATALOG'),
    z.literal('SHOW'),
    z.string(),
  ]),
  parentId: z.string(),
  // path: z.string(),
  children: z.array(z.any()),
  // publishing: Publishing,
  products: z.array(Product),
  publisherId: z.number().optional(),
  ['description-short']: z.string().optional(),
  // imagePack: z.string().optional(),
  title: z.string().optional(),
  people: z.string().optional(),
  labels: z.string().optional(),
  ['age-rating']: z.string().optional(),
  tags: z.string().optional(),
  season: z.number().optional(),
});

const Element = z.object({
  id: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  'description-short': z.string().optional(),
  parentId: z.string().optional(),
  images: Image.optional(),
  timestamp: z.string().optional(),
  createTime: z.string().optional(),
  updateTime: z.string().optional(),
  contentType: z.string(),
});

const CuratedAsset = Element.merge(
  z.object({
    contentType: z.literal('asset'),
    content: Asset.optional(),
  }),
);

export type CuratedAssetType = z.infer<typeof CuratedAsset>;

const CuratedCategory = Element.merge(
  z.object({
    contentType: z.literal('category'),
    content: CategoryElement.optional(),
  }),
);

export type CuratedCategoryType = z.infer<typeof CuratedCategory>;

const Url = Element.merge(
  z.object({
    contentType: z.literal('url'),
    href: z.string().optional(),
  }),
);

export type UrlType = z.infer<typeof Url>;

const MenuItem = Element.merge(
  z.object({
    contentType: z.literal('menu_item'),
    url: z.string().optional(),
    'icon-name': z.string().optional(),
  }),
);

export type MenuItemType = z.infer<typeof MenuItem>;

const Menu: z.ZodType<MenuType> = Element.merge(
  z.object({
    contentType: z.literal('menu'),
    'layout-menu-position': z
      .union([z.literal('Header'), z.literal('Footer')])
      .optional(),
    elements: z.lazy(() => z.array(z.union([MenuItem, Menu]))).optional(),
  }),
);

export type MenuType = {
  contentType: 'menu';
  'layout-menu-position'?: 'Header' | 'Footer';
  elements?: (MenuType | MenuItemType)[];
  url?: string;
} & z.infer<typeof Element>;

const basePageSchema = Element.merge(
  z.object({
    contentType: z.literal('page'),
    'page-slug': z.string().optional(),
    'authentication-required': z.boolean().optional(),
    'meta-title': z.string().optional(),
    'meta-description': z.string().optional(),
  }),
);

export type PageType = z.infer<typeof basePageSchema> & {
  elements?: (
    | ListType
    | PageType
    | CarouselType
    | SmartListType
    | MenuType
    | MenuItemType
    | CuratedAssetType
    | CuratedCategoryType
    | UrlType
    | RecommendedType
    | SearchType
  )[];
};

const Page: z.ZodType<PageType> = basePageSchema.extend({
  elements: z
    .lazy(() =>
      z.array(
        z.union([
          List,
          Page,
          Carousel,
          SmartList,
          Menu,
          MenuItem,
          CuratedAsset,
          CuratedCategory,
          Url,
          Recommended,
          Search,
        ]),
      ),
    )
    .optional(),
});

const baseListSchema = Element.merge(
  z.object({
    contentType: z.union([
      z.literal('contentpanel'),
      z.literal('list_smart'),
      z.literal('list'),
    ]),
    alias: z.string().optional(),
    'authentication-enabled': z.boolean().optional(),
    content: z.union([Asset, Category]).optional(),
    'layout-container-type': z
      .union([
        z.literal('Top10'),
        z.literal('Continue Watching'),
        z.literal('Shows'),
        z.literal('Movies'),
        z.literal('Clips'),
        z.literal('Channels'),
        z.literal('Events'),
        z.literal('Coming Soon'),
        z.literal('Promotion'),
        z.literal('FAQ'),
        // z.string(),
      ])
      .optional(),
    'layout-override': z
      .union([
        z.literal('grid'),
        z.literal('carousel'),
        z.literal('carousel-hero'),
      ])
      .optional(),
    'layout-carousel-items-per-page': z.number().optional(),
    labels: z.string().optional(),
    'theme-brand-color': z.string().optional(),
    meta: z
      .object({
        totalPages: z.number(),
        pageSize: z.number(),
        numberOfHits: z.number(),
      })
      .optional(),
  }),
);

const SmartList = baseListSchema.merge(
  z.object({
    contentType: z.literal('list_smart'),
    elements: z.array(z.union([CuratedAsset, CuratedCategory])).optional(),
  }),
);

export type SmartListType = z.infer<typeof SmartList>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Playlist = baseListSchema.merge(
  z.object({
    contentType: z.string(),
    elements: z.array(CuratedAsset).optional(),
  }),
);

export type PlaylistType = z.infer<typeof Playlist>;

const Recommended = baseListSchema.merge(
  z.object({
    contentType: z.literal('recommended'),
  }),
);

export type RecommendedType = z.infer<typeof Recommended>;

const Search = baseListSchema.merge(
  z.object({
    contentType: z.literal('search'),
  }),
);

export type SearchType = z.infer<typeof Search>;

const Carousel = baseListSchema.merge(
  z.object({
    contentType: z.literal('carousel'),
    elements: z.array(z.union([CuratedAsset, CuratedCategory])).optional(),
  }),
);

export type CarouselType = z.infer<typeof Carousel>;

export type ListType = z.infer<typeof baseListSchema> & {
  elements?: (
    | ListType
    | CarouselType
    | SmartListType
    | PageType
    | MenuType
    | MenuItemType
    | CuratedAssetType
    | CuratedCategoryType
    | UrlType
    | RecommendedType
    | SearchType
  )[];
};

const List: z.ZodType<ListType> = baseListSchema.extend({
  elements: z
    .lazy(() =>
      z.array(
        z.union([
          List,
          Carousel,
          SmartList,
          Page,
          Menu,
          MenuItem,
          CuratedAsset,
          CuratedCategory,
          Url,
          Recommended,
          Search,
        ]),
      ),
    )
    .optional(),
});

const ErrorInfo = z.object({
  title: z.string(),
  detail: z.string(),
  code: z.string(),
});

const responseValidator = z.object({
  data: z.array(List).optional(),
  errors: z.array(ErrorInfo).optional(),
});
